<template>
  <div class="tenant-project-guideline-section">
    <ul
      id="tenant-project-guideline-accordion"
      class="accordion accordion-customize"
      :class="enableEdit ? '' : 'view-only-guideline'"
      data-accordion
      data-deep-link="true"
      data-allow-all-closed="true"
      data-multi-expand="true"
      data-update-history="true"
      data-deep-link-smudge="500"
    >
      <li
        v-for="(section, index) in parsedGuidelines"
        :key="section.id"
        class="accordion-item accordion-item-customize"
        data-accordion-item
      >
        <a
          :href="`#project-guideline-section-${index}`"
          class="accordion-title accordion-customize-title"
          :class="{'with-checkbox': enableEdit}"
        >
          <span>{{ displayOrder(section) }}</span>
          <span class="section-title">{{ section.value }}</span>
        </a>
        <div
          :id="`project-guideline-section-${index}`"
          class="accordion-content accordion-customize-content"
          data-tab-content
        >
          <ul class="section-list">
            <li
              v-for="(line, lineIndex) in section.sectionContent"
              :key="line.id"
              class="section-list-title"
            >
              <div
                class="section-list-item"
                :class="{'with-checkbox': enableEdit}"
              >
                <div>
                  <div class="section-list-item__number">
                    {{ `${lineIndex + 1}.` }}
                  </div>
                  <pre class="section-list-item__value">{{ line.value }}</pre>
                </div>
                <template v-if="isHKlandUser">
                  <div
                    v-for="(rec, lIndex) in line.actionTexts"
                    :key="`${line.id}_${lIndex}`"
                    class="update-history__text"
                  >
                    <span>{{ rec }}</span>
                  </div>
                </template>
              </div>
              <input
                v-show="enableEdit"
                class="section-list-checkbox"
                type="checkbox"
                :checked="isInTechComment(line.itemId, section.itemId)"
                :disabled="isInTechComment(line.itemId, section.itemId) && !isCheckedByCurrentUser(line.itemId, section.itemId)"
                @change="event => changeTechComment(section, line, event.target.checked)"
              >
            </li>
          </ul>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import dateUtil from '@/utils/dateUtil'
import numberUtils from '@/utils/numberUtils'
import { getGuidelines } from '@/api/settings/request'
import { GUIDELINE_STATUS, tenantProjectTypes } from '@/utils/locale'
import * as guidelineUtils from '@/utils/guideline'
import { buildLastActionText } from '@/utils/tenantProjectGuidelineUtils'
import { CommentActionTypes } from './submissions/consts'
import { GUIDELINE_TYPE } from '@/modules/settings/engineers/consts'

export default {
  name: 'TenantProjectGuideline',
  props: {
    enableEdit: Boolean,
  },
  setup () {
    return {
      displayOrder: guidelineUtils.displayOrder,
    }
  },
  data () {
    return {
      numberUtils,
      classifiedGuidelines: [],
      dateUtil,
    }
  },
  computed: {
    ...mapGetters('user', [
      'currentUser',
    ]),
    ...mapGetters('tenantProject',
      ['currentTenantProject', 'activeSubmission', 'activeSubmissionIndex', 'currentTenantSubmission']),
    ...mapGetters('user', ['isHKlandUser']),
    comments () {
      return lodash.get(this.activeSubmission, 'techComment.comments') || []
    },
    type () {
      if (this.currentTenantProject.type === tenantProjectTypes.Fitout) {
        return GUIDELINE_TYPE.FITOUT_LETTER
      } else if (this.currentTenantProject.type === tenantProjectTypes.Reinstatement) {
        return GUIDELINE_TYPE.REINSTATEMENT_LETTER
      }
      return ''
    },
    parsedGuidelines () {
      const allSubmissionsHistories = this.currentTenantProject.submissions
        .reduce((rs, item) => {
          item.actionHistory && rs.push(...item.actionHistory)
          return rs
        }, [])
      return this.classifiedGuidelines.map(section => {
        section.sectionContent.forEach(line => {
          const actionHistories = allSubmissionsHistories.filter(rec => rec.guidelineId === line.itemId && !rec.isNewRec && rec.actionType === CommentActionTypes.CHECK && !rec.isDeleteRec)
          line.actionHistories = lodash.orderBy(actionHistories, ['batchOrder', 'updatedAt'], ['desc', 'desc'])
          // #TODO <sam_liang> Can't change locale language
          line.actionTexts = line.actionHistories.map(rec => {
            const actionByText = buildLastActionText(rec)
            return this.$t('tenantProjects.submission.guideline.actionBatch', [actionByText, rec.batchOrder + 1])
          })
        })
        return section
      })
    },
  },
  watch: {
    classifiedGuidelines () {
      this.refreshPage()
    },
  },
  async mounted () {
    const data = await getGuidelines(GUIDELINE_STATUS.PUBLISHED, this.type)
    const tmpGuidelines = data.guidelines || []
    tmpGuidelines && tmpGuidelines.forEach(section => {
      if (section.sectionContent && Array.isArray(section.sectionContent)) {
        section.sectionContent = lodash.orderBy(section.sectionContent, item => {
          return Number(item.order)
        })
      }
    })
    this.classifiedGuidelines = lodash.orderBy(tmpGuidelines, 'order')
  },
  destroyed () {
    if (this.tenantProjectGuidelineAccordion && Object.keys(this.tenantProjectGuidelineAccordion).length > 0) {
      if ($('#tenant-project-guideline-accordion').childElementCount > 0) {
        this.tenantProjectGuidelineAccordion.destroy()
      }
      delete this.tenantProjectGuidelineAccordion
    }
  },
  methods: {
    ...mapActions('tenantProject', [
      'addTechCommentLine',
      'removeTechCommentLine',
    ]),
    initAccordion () {
      if (this.tenantProjectGuidelineAccordion && Object.keys(this.tenantProjectGuidelineAccordion).length > 0) {
        if ($('#tenant-project-guideline-accordion').childElementCount > 0) {
          this.tenantProjectGuidelineAccordion.destroy()
        }
        delete this.tenantProjectGuidelineAccordion
      }
      if (!this.tenantProjectGuidelineAccordion) {
        this.tenantProjectGuidelineAccordion = new Foundation.Accordion($('#tenant-project-guideline-accordion'))
      }
    },
    refreshPage () {
      setTimeout(this.initAccordion, 0)
    },
    isInTechComment (lineItemId, sectionItemId) {
      return this.comments.some(comment => comment.itemId === sectionItemId &&
        comment.sectionContent.some(subComment => subComment.itemId === lineItemId))
    },
    async changeTechComment (section, line, checked) {
      if (checked) {
        const comment = lodash.cloneDeep(line)
        await this.addTechCommentLine({section, line: comment})
      } else {
        this.removeTechCommentLine({section, line})
      }
    },
    isCheckedByCurrentUser (lineItemId, sectionItemId) {
      let isCheckedByCurrentUser = false
      this.comments.forEach(comment => {
        if (comment.itemId === sectionItemId) {
          comment.sectionContent.forEach(subComment => {
            if (subComment.itemId !== lineItemId) {
              return false
            }
            if (deep(this.activeSubmission, 'actionHistory').length > 0) {
              const subCommentCheckActions = this.activeSubmission.actionHistory.filter(action =>
                action.actionType === CommentActionTypes.CHECK && action.guidelineId === lineItemId)
              subCommentCheckActions && subCommentCheckActions.forEach(action => {
                if (action.user.id === this.currentUser.id) {
                  isCheckedByCurrentUser = true
                }
              })
            }
          })
        }
      })
      return isCheckedByCurrentUser
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~styles/tender/view";
@import "~styles/accordion";
@import "~styles/tenants/guideline";
.update-history__text {
  & span {
    float: right;
  }
  color: #878787;
  font-size: 12px;
  padding: 10px;
  font-style: italic;
}
</style>
